import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Box, Skeleton } from '@mui/material';
import { styled } from '@mui/system';
import TopBar from './TopBar';
import Schema from './Schema';
import { useNavigate } from 'react-router';
import { send_request } from '../../utils/Request';

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
}));

const ContentWrapper = styled(Box)({
  maxWidth: '1600px',
  margin: '0 auto',
  padding: '16px 20px',
});

const HeaderSection = styled(Box)({
  marginBottom: '16px',
});

const DiffSection = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
}));

const LoadingContainer = styled(Box)({
  maxWidth: '1600px',
  margin: '0 auto',
  padding: '16px 20px',
});

const SkeletonHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '24px',
});

const SkeletonCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  padding: '24px',
}));

function WorkflowSchema() {
  const projectId = window.location.pathname.split('/').pop();
  const navigate = useNavigate();

  // Loading states
  const [loading, setLoading] = useState(true);
  const [compareLoad, setCompareLoad] = useState(false);

  // Data
  const [allProjects, setAllProjects] = useState(null);
  const [workflowName, setWorkflowName] = useState(null);

  // View mode
  const [view, setView] = useState('split_view');

  // Selections
  const [selectionOne, setSelectionOne] = useState(null);
  const [selectionTwo, setSelectionTwo] = useState(null);

  // Translation data
  const [jsonOne, setJsonOne] = useState(null);
  const [jsonTwo, setJsonTwo] = useState(null);

  // Cache for translated workflows to avoid duplicate calls
  const translationCache = useRef({});

  // Defer diff rendering until stable
  const [deferredRender, setDeferredRender] = useState(false);

  useEffect(() => {
    const initialiseWorkflowSchema = async () => {
      setLoading(true);

      const URL = `project-service/project/all_project_versions/${projectId}`;
      let json = await send_request(URL, null, null, 'GET');
      if (!json || json.status !== 200) return;

      let projects = json.data;
      setAllProjects(projects);

      // Check query params
      const params = new URLSearchParams(location.search);
      const selected = params.get('selected');
      const compareWith = params.get('compareWith');

      if (selected && compareWith) {
        const v1 = projects.find(item => String(item.version) === String(selected));
        const v2 = projects.find(item => String(item.version) === String(compareWith));

        if (!v1 || !v2) {
          await handleDefaultCase(projects);
          return;
        }

        setSelectionOne(`${v1.projectStatus}_version_${v1.version}`);
        setSelectionTwo(`${v2.projectStatus}_version_${v2.version}`);
        setLoading(false);

        await handleCompareJson(selected, compareWith);
        return;
      } else if (selected) {
        const v1 = projects.find(item => String(item.version) === String(selected));

        if (!v1) {
          await handleDefaultCase(projects);
          return;
        }

        setSelectionOne(`${v1.projectStatus}_version_${v1.version}`);
        setSelectionTwo(`${v1.projectStatus}_version_${v1.version}`);
        setLoading(false);

        await handleCompareJson(selected, selected);
        return;
      }

      await handleDefaultCase(projects);
    };
    initialiseWorkflowSchema();
  }, []);

  const handleDefaultCase = async projects => {
    // Default: compare latest with previous version (if exists)
    const latestProject = projects[projects.length - 1];
    const previousProject = projects.length > 1 ? projects[projects.length - 2] : latestProject;

    const data = await getTranslatedWorkflow(projectId, latestProject.version);
    if (!data) return;

    setWorkflowName(data.name);

    setSelectionOne(`${previousProject.projectStatus}_version_${previousProject.version}`);
    setSelectionTwo(`${latestProject.projectStatus}_version_${latestProject.version}`);

    // If same version, use same data
    if (previousProject.version === latestProject.version) {
      setJsonOne(data);
      setJsonTwo(data);
    } else {
      const previousData = await getTranslatedWorkflow(projectId, previousProject.version);
      setJsonOne(previousData);
      setJsonTwo(data);
    }

    // Update URL
    const params = new URLSearchParams(window.location.search);
    params.set('selected', previousProject.version);
    params.set('compareWith', latestProject.version);
    navigate({ search: params.toString() }, { replace: true });

    setLoading(false);

    // Defer diff render for smoother UX
    setTimeout(() => setDeferredRender(true), 50);
  };

  const goBack = () => {
    navigate(`/project/canvas/${projectId}`);
  };

  const getTranslatedWorkflow = useCallback(async (projectId, version) => {
    const cacheKey = `${projectId}_${version}`;

    // Return cached version if available
    if (translationCache.current[cacheKey]) {
      return translationCache.current[cacheKey];
    }

    const url = `project-service/project-ai/assistant/retrieve_workflow_translation/${projectId}?version=${version}`;
    const json = await send_request(url, null, null, 'GET');

    // Cache the result
    if (json?.data) {
      translationCache.current[cacheKey] = json.data;
    }

    return json?.data;
  }, []);

  const handleCompareJson = async (selectedV1, selectedV2) => {
    try {
      setCompareLoad(true);
      setDeferredRender(false);

      const versionOne = selectedV1 ? selectedV1 : selectionOne.split('_')[2];
      const versionTwo = selectedV2 ? selectedV2 : selectionTwo.split('_')[2];

      // Avoid duplicate call if versions are the same
      if (versionOne === versionTwo) {
        const data = await getTranslatedWorkflow(projectId, versionOne);
        setJsonOne(data);
        setJsonTwo(data);

        // Set workflow name if not already set
        if (!workflowName && data?.name) {
          setWorkflowName(data.name);
        }
      } else {
        // Fetch both in parallel
        const [data1, data2] = await Promise.all([
          getTranslatedWorkflow(projectId, versionOne),
          getTranslatedWorkflow(projectId, versionTwo),
        ]);
        setJsonOne(data1);
        setJsonTwo(data2);

        // Set workflow name from newer version
        if (!workflowName && data2?.name) {
          setWorkflowName(data2.name);
        }
      }

      // Update URL params
      if (!selectedV1 && !selectedV2) {
        const params = new URLSearchParams(window.location.search);
        params.set('selected', versionOne);
        params.set('compareWith', versionTwo);
        navigate({ search: params.toString() }, { replace: true });
      }

      setCompareLoad(false);

      // Defer diff render for smoother UX
      setTimeout(() => setDeferredRender(true), 50);
    } catch (e) {
      console.error('Error comparing versions:', e);
      setCompareLoad(false);
    }
  };

  // Extract version info for display
  const versionInfo = useMemo(() => {
    if (!allProjects || !selectionOne || !selectionTwo) return null;

    const v1Num = selectionOne.split('_')[2];
    const v2Num = selectionTwo.split('_')[2];

    const v1Project = allProjects.find(p => String(p.version) === v1Num);
    const v2Project = allProjects.find(p => String(p.version) === v2Num);

    return { v1: v1Project, v2: v2Project };
  }, [allProjects, selectionOne, selectionTwo]);

  const renderSkeleton = () => (
    <LoadingContainer>
      <SkeletonHeader>
        <Skeleton
          variant="circular"
          width={40}
          height={40}
          sx={{ bgcolor: 'action.hover', flexShrink: 0 }}
        />
        <Skeleton
          variant="rounded"
          width={200}
          height={32}
          sx={{ bgcolor: 'action.hover', borderRadius: '8px' }}
        />
      </SkeletonHeader>

      <SkeletonCard sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Skeleton
            variant="rounded"
            width={280}
            height={80}
            sx={{ bgcolor: 'action.hover', borderRadius: '8px' }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
            <Skeleton variant="circular" width={24} height={24} sx={{ bgcolor: 'action.hover' }} />
          </Box>
          <Skeleton
            variant="rounded"
            width={280}
            height={80}
            sx={{ bgcolor: 'action.hover', borderRadius: '8px' }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Skeleton
            variant="rounded"
            width={120}
            height={40}
            sx={{ bgcolor: 'action.hover', borderRadius: '8px' }}
          />
          <Skeleton
            variant="rounded"
            width={100}
            height={40}
            sx={{ bgcolor: 'action.hover', borderRadius: '8px' }}
          />
        </Box>
      </SkeletonCard>

      <SkeletonCard>
        <Skeleton
          variant="rounded"
          width="100%"
          height={600}
          sx={{ bgcolor: 'action.hover', borderRadius: '8px' }}
        />
      </SkeletonCard>
    </LoadingContainer>
  );

  if (loading) {
    return (
      <PageContainer>
        {renderSkeleton()}
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentWrapper>
        <HeaderSection>
          <TopBar
            goBack={goBack}
            workflowName={workflowName}
            allProjects={allProjects}
            handleCompareJson={handleCompareJson}
            selectionOne={selectionOne}
            selectionTwo={selectionTwo}
            setSelectionOne={setSelectionOne}
            setSelectionTwo={setSelectionTwo}
            compareLoad={compareLoad}
            view={view}
            setView={setView}
          />
        </HeaderSection>

        <DiffSection>
          <Schema
            jsonOne={jsonOne}
            jsonTwo={jsonTwo}
            view={view}
            compareLoad={compareLoad}
            deferredRender={deferredRender}
            versionInfo={versionInfo}
          />
        </DiffSection>
      </ContentWrapper>
    </PageContainer>
  );
}

export default WorkflowSchema;
